import React from 'react';
import { useButtonLinks } from '../hooks/useButtonLinks';
import { useTranslation } from '../hooks/useTranslation';

const WhyRiverPrime = () => {
  const { handleButtonClick } = useButtonLinks();
  const { t } = useTranslation();
  return (
    <div className="why-river-prime-page">
      {/* Hero Section */}
      <section className="why-hero">
        <div className="container">
          <div className="why-hero-content">
            <h1 className="why-title">{t('whyRiverPrime.hero.title')}</h1>
            <p className="why-subtitle">
              {t('whyRiverPrime.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* River Prime's Story Section */}
      <section className="river-prime-story">
        <div className="container">
          <h2 className="section-title">{t('whyRiverPrime.story.title')}</h2>
          <div className="story-content">
            <p className="story-intro">
              {t('whyRiverPrime.story.intro')}
            </p>
            <p className="story-paragraph">
              {t('whyRiverPrime.story.paragraph1')}
            </p>
            <p className="story-paragraph">
              {t('whyRiverPrime.story.paragraph2')}
            </p>
            <blockquote className="story-quote">
              "{t('whyRiverPrime.story.quote')}"
            </blockquote>
          </div>
        </div>
      </section>

      {/* River Prime in Numbers Section */}
      <section className="river-prime-numbers">
        <div className="container">
          <h2 className="section-title">{t('whyRiverPrime.numbers.title')}</h2>
          <div className="numbers-grid">
            <div className="number-card">
              <div className="number-value">5</div>
              <div className="number-label">{t('whyRiverPrime.numbers.stats.execution')}</div>
            </div>
            <div className="number-card">
              <div className="number-value">12</div>
              <div className="number-label">{t('whyRiverPrime.numbers.stats.liquidity')}</div>
            </div>
            <div className="number-card">
              <div className="number-value">12000</div>
              <div className="number-label">{t('whyRiverPrime.numbers.stats.orders')}</div>
            </div>
            <div className="number-card">
              <div className="number-value">50</div>
              <div className="number-label">{t('whyRiverPrime.numbers.stats.countries')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Global River Prime Section */}
      <section className="global-river-prime">
        <div className="container">
          <h2 className="section-title">{t('whyRiverPrime.global.title')}</h2>
          <p className="section-description">
            {t('whyRiverPrime.global.description1')}
          </p>
          <p className="section-description">
            {t('whyRiverPrime.global.description2')}
          </p>
          
          <div className="global-features">
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-balance-scale"></i>
              </div>
              <h3>{t('whyRiverPrime.global.features.equalChances.title')}</h3>
              <p>
                {t('whyRiverPrime.global.features.equalChances.description')}
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3>{t('whyRiverPrime.global.features.security.title')}</h3>
              <p>
                {t('whyRiverPrime.global.features.security.description')}
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-users"></i>
              </div>
              <h3>{t('whyRiverPrime.global.features.openspace.title')}</h3>
              <p>
                {t('whyRiverPrime.global.features.openspace.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who Can Trade Section */}
      <section className="who-can-trade">
        <div className="container">
          <h2 className="section-title">{t('whyRiverPrime.whoCanTrade.title')}</h2>
          <p className="section-subtitle">{t('whyRiverPrime.whoCanTrade.subtitle')}</p>
          
          <div className="trader-types">
            <div className="trader-card">
              <div className="trader-icon">
                <i className="fas fa-seedling"></i>
              </div>
              <h3>{t('whyRiverPrime.whoCanTrade.types.newTrader.title')}</h3>
              <p>
                {t('whyRiverPrime.whoCanTrade.types.newTrader.description')}
              </p>
            </div>
            
            <div className="trader-card">
              <div className="trader-icon">
                <i className="fas fa-chart-bar"></i>
              </div>
              <h3>{t('whyRiverPrime.whoCanTrade.types.knowledgeable.title')}</h3>
              <p>
                {t('whyRiverPrime.whoCanTrade.types.knowledgeable.description')}
              </p>
            </div>
            
            <div className="trader-card">
              <div className="trader-icon">
                <i className="fas fa-lightbulb"></i>
              </div>
              <h3>{t('whyRiverPrime.whoCanTrade.types.interested.title')}</h3>
              <p>
                {t('whyRiverPrime.whoCanTrade.types.interested.description')}
              </p>
              <div className="learn-more">
                <strong>{t('whyRiverPrime.whoCanTrade.types.interested.learnMore')}</strong>
              </div>
            </div>
            
            <div className="trader-card">
              <div className="trader-icon">
                <i className="fas fa-user-graduate"></i>
              </div>
              <h3>{t('whyRiverPrime.whoCanTrade.types.expert.title')}</h3>
              <p>
                {t('whyRiverPrime.whoCanTrade.types.expert.description')}
              </p>
            </div>
            
            <div className="trader-card">
              <div className="trader-icon">
                <i className="fas fa-mobile-alt"></i>
              </div>
              <h3>{t('whyRiverPrime.whoCanTrade.types.technology.title')}</h3>
              <p>
                {t('whyRiverPrime.whoCanTrade.types.technology.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="trading-advantages">
        <div className="container">
          <h2 className="section-title">{t('whyRiverPrime.advantages.title')}</h2>
          <div className="advantages-grid">
            <div className="advantage-item">
              <i className="fas fa-dollar-sign"></i>
              <span>{t('whyRiverPrime.advantages.items.lowCost')}</span>
            </div>
            <div className="advantage-item">
              <i className="fas fa-desktop"></i>
              <span>{t('whyRiverPrime.advantages.items.platform')}</span>
            </div>
            <div className="advantage-item">
              <i className="fas fa-globe"></i>
              <span>{t('whyRiverPrime.advantages.items.markets')}</span>
            </div>
            <div className="advantage-item">
              <i className="fas fa-cogs"></i>
              <span>{t('whyRiverPrime.advantages.items.technology')}</span>
            </div>
            <div className="advantage-item">
              <i className="fas fa-lock"></i>
              <span>{t('whyRiverPrime.advantages.items.security')}</span>
            </div>
            <div className="advantage-item">
              <i className="fas fa-book"></i>
              <span>{t('whyRiverPrime.advantages.items.guides')}</span>
            </div>
            <div className="advantage-item">
              <i className="fas fa-heart"></i>
              <span>{t('whyRiverPrime.advantages.items.community')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Partners Section */}
      <section className="strategic-partners">
        <div className="container">
          <h2 className="section-title">{t('whyRiverPrime.partners.title')}</h2>
          <div className="partners-content">
            <p className="partners-description">
              {t('whyRiverPrime.partners.description')}
            </p>
            <div className="partners-placeholder">
              <div className="partner-logo">Partner 1</div>
              <div className="partner-logo">Partner 2</div>
              <div className="partner-logo">Partner 3</div>
              <div className="partner-logo">Partner 4</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="why-cta">
        <div className="container">
          <div className="cta-content">
            <h2>{t('whyRiverPrime.cta.title')}</h2>
            <p>{t('whyRiverPrime.cta.description')}</p>
            <div className="cta-buttons">
              <button 
                className="btn btn-primary btn-large"
                onClick={(e) => handleButtonClick('getStarted', e)}
              >
                {t('whyRiverPrime.cta.startButton')}
              </button>
              <button 
                className="btn btn-secondary btn-large"
                onClick={(e) => handleButtonClick('tryDemo', e)}
              >
                {t('whyRiverPrime.cta.demoButton')}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyRiverPrime;